<template>
  <div class="evaluations-wrapper">
    <div
      v-for="(evaluationArr, discipline, idx) in groupedEvaluations"
      :key="`discipline-${idx}`"
      class="mb-3"
    >
      <div class="bg-gray-4 rounded px-4 py-2 mb-n2">
        <span class="font-weight-bold">
          {{ discipline }}
        </span>
      </div>
      <custom-callout
        v-for="(item, index) in evaluationArr"
        :key="`evaluation-${index}`"
        class-main="rounded mt-3"
        :has-toggle="showToggle(item)"
        :has-gradient="showToggle(item)"
      >
        <div class="row mx-0 align-items-center justify-content-between">
          <div class="col-12 col-md-2 mb-3 mb-md-0">
            <span class="d-block font-weight-bold">
              {{ item.source }}
            </span>
            <span class="font-14">{{ item.disciplineTypeName }}</span>
          </div>
          <div class="col-12 col-md-2">
            <span class="data-title">Fecha:</span>
            {{ item.evaluationDate | date }}
          </div>
          <div class="col-12 col-md-2">
            <span class="data-title">Estatus:</span>
            <div class="d-flex align-items-center">
              <em
                class="fas fa-circle font-10 mr-1"
                :class="`color-${getStatusColor(item)}`"
              />
              {{ getStatusDesc(item) | empty }}
            </div>
          </div>
          <div
            v-if="item.sourceId != sourceTypes.RemedioProvisional"
            class="col-12 col-md-3"
          >
            <span class="data-title">Administrado por:</span>
            {{ item.administeredBy | empty }}
          </div>
          <div v-else class="col-12 col-md-3">
            <span class="data-title">Especializado por:</span>
            {{ item.administeredBy | empty }}
          </div>
          <div class="col-12 col-md-3 justify-content-end d-flex">
            <dropdown :items="actionItems(item)" />
          </div>
        </div>
        <template #edition>
          <div class="bg-secondary-3 p-2">
            <div class="bg-white rounded p-3">
              <icon-view
                :has-border="false"
                icon="fas fa-info-circle color-orange"
                icon-size="1rem"
                title="Información Adicional"
                variant="custom-icon-view"
              />
              <div class="row mx-0">
                <template v-if="item.sourceId === sourceTypes.MipeCita">
                  <div class="col-12 col-md-3 mb-2">
                    <span class="data-title">Número de Cita:</span>
                    {{ item.formatedAppointmentId }}
                  </div>
                  <div class="col-12 col-md-3">
                    <span class="data-title">Estatus:</span>
                    {{ item.appointmentStatusName }}
                  </div>
                </template>
                <template
                  v-if="item.sourceId === sourceTypes.RemedioProvisional"
                >
                  <div class="col-12 col-md-3">
                    <span class="data-title">Número de Solicitud:</span>
                    {{ item.requestNumber }}
                  </div>
                  <div class="col-12 col-md-3">
                    <span class="data-title">Número de Contrato:</span>
                    {{ item.contractNumber }}
                  </div>
                </template>
                <div class="col-12 col-md-6">
                  <span class="data-title">Proveedor:</span>
                  {{ item.providerName }}
                </div>
                <div
                  v-if="item.sourceId === sourceTypes.MipeCita"
                  class="col-12"
                >
                  <span class="data-title">Comentario:</span>
                  {{ item.appointmentComment }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </custom-callout>
    </div>
    <div class="container-table-striped w-100 mt-4">
      <CustomModal
        v-model="showPreviousDate"
        save-text="Si"
        cancel-text="No"
        title="Cambiar Fecha"
        @cancel="closeModals"
        @save="changePreviousDate"
      >
        <div :class="showPreviousDate ? '' : 'text-center'">
          <div v-if="showPreviousDate" class="mt-2 col-12">
            <ValidationObserver ref="observer" novalidate>
              <div class="row w-100">
                <ValidationProvider
                  v-slot="{ errors }"
                  class="w-100 text-left info"
                  name="Fecha Previa"
                  rules="required"
                >
                  <CustomDatePicker
                    id="previous-date"
                    v-model="previousDate"
                    :rows="1"
                    label="Fecha Previa:"
                    :error="errors[0]"
                    :custom-error-msg="'Campo Requerido.'"
                    name="previous-date"
                  />
                </ValidationProvider>
              </div>
            </ValidationObserver>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        v-model="confirmDelete"
        save-text="Si"
        cancel-text="No"
        title="Confirmación"
        @cancel="closeModals"
        @save="deleteEvaluation"
      >
        <div :class="showDeleteComment ? '' : 'text-center'">
          ¿Está seguro que desea eliminar el documento de la evaluación?

          <div v-if="showDeleteComment" class="mt-2 col-12">
            <ValidationObserver ref="observer" novalidate>
              <div class="row w-100">
                <ValidationProvider
                  v-slot="{ errors }"
                  class="w-100 text-left info"
                  name="Comentario"
                  rules="required"
                >
                  <CustomTextArea
                    id="comment"
                    v-model="deleteComment"
                    :rows="1"
                    label="Comentario:"
                    :maxlength="500"
                    :error="errors[0]"
                    :custom-error-msg="'Campo Requerido.'"
                    name="comment"
                  />
                </ValidationProvider>
              </div>
            </ValidationObserver>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        v-model="confirmStatusChange"
        save-text="Si"
        cancel-text="No"
        title="Confirmación"
        @cancel="closeModals"
        @save="changeDeterminationToPending"
      >
        <div :class="showStatusComment ? '' : 'text-center'">
          ¿Está seguro que desea cambiar el status de la evaluación a pendiente?

          <div v-if="showStatusComment" class="mt-2 col-12">
            <ValidationObserver ref="observer" novalidate>
              <div class="row w-100">
                <ValidationProvider
                  v-slot="{ errors }"
                  class="w-100 text-left info"
                  name="Comentario"
                  rules="required"
                >
                  <CustomTextArea
                    id="comment"
                    v-model="statusComment"
                    :rows="1"
                    label="Comentario:"
                    :maxlength="500"
                    :error="errors[0]"
                    :custom-error-msg="'Campo Requerido.'"
                    name="comment"
                  />
                </ValidationProvider>
              </div>
            </ValidationObserver>
          </div>
        </div>
      </CustomModal>
      <CustomModal
        v-model="showCantDelete"
        :show-cancel="false"
        save-text="Ok"
        cancel-text="Cerrar"
        title="Evaluación"
        @save="closeModals"
      >
        <div class="text-center">
          No se pueden eliminar documentos que estan relacionados con un PEI.
        </div>
      </CustomModal>
      <CustomModal
        v-model="showEvalModal"
        :show-cancel="false"
        title="Aviso"
        save-text="Ok"
        @save="closeModals"
      >
        <div class="text-center">
          No se encontró documento para esa evaluación.
        </div>
      </CustomModal>
    </div>
  </div>
</template>

<script>
import CustomCallout from 'custom-callout';
import alert from '@/mixin/alert.mixin';
import store from '@/store/index';
import generalService from '@/services/pei/general';
import reportService from '@/services/report';
import { scopes } from '../scopes';
import studentEvaluationService from '@/modules/student-evaluations/api';
import evaluationSourceTypes from '@/utils/constants/student/evaluationSourceTypes';
import DiscussionStatus from '@/utils/constants/student/discussionStatus';
import ElegibilityDetermination from '@/utils/constants/student/elegibilityDetermination';
import disciplineTypes from '@/utils/constants/disciplineTypes.js';
import { discussionStatuses } from '@/utils/constants/evaluation-discussion/discussionStatusCode';
import Dropdown from 'dropdown';
import IconView from 'icon-view';

import { CustomTextArea, CustomDatePicker } from '@/components/common';
import CustomModal from '@/components/common/CustomModal';

const PreschoolInterventionResultSummaryId = -1;

export default {
  name: 'StudentEvaluationList',
  mixins: [alert],
  components: {
    CustomModal,
    CustomTextArea,
    CustomDatePicker,
    CustomCallout,
    Dropdown,
    IconView,
  },
  props: {
    evaluations: {
      type: Array,
      default: () => [],
    },
    readOnly: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      showStatusComment: true,
      sourceTypes: Object.freeze({
        Previa: 1,
        MipeCita: 2,
        RemedioProvisional: 3,
      }),
      showCantDelete: false,
      confirmDelete: false,
      showEvalModal: false,
      deleteComment: null,
      showDeleteComment: false,
      showPreviousDate: false,
      previousDate: null,
      currentEvaluation: null,
      confirmStatusChange: false,
      statusComment: null,
    };
  },
  computed: {
    showToggle() {
      return ({ sourceId }) =>
        [
          this.sourceTypes.RemedioProvisional,
          this.sourceTypes.MipeCita,
        ].includes(sourceId);
    },
    actionItems() {
      return (evaluation) => {
        const showChangeDate =
          this.canChangePreviousDate &&
          evaluation.source === this.sourceNameTypes.Previa;
        const canEliminateEvaluation = ![
          evaluationSourceTypes.MiPE_Cita,
          evaluationSourceTypes.Remedio_Provisional,
        ].some((sourceId) => sourceId === evaluation.sourceId);

        if (
          this.readOnly ||
          evaluation.disciplineTypeId == PreschoolInterventionResultSummaryId
        ) {
          return [
            {
              label: 'Documento de Evaluación',
              icon: 'fas fa-file-download color-green',
              visible: evaluation.hasDocument,
              click: () =>
                evaluation.disciplineTypeId !=
                PreschoolInterventionResultSummaryId
                  ? this.downloadDocument({
                      id: evaluation.studentEvaluationId,
                      type: 1,
                    })
                  : this.$ApiGet(
                      `/api/report/pei/ind7report/${evaluation.formatedAppointmentId}`
                    ) // Is PeiId
                      .then((res) =>
                        generalService.documentDownloadReport(
                          res.data.content,
                          res.data.filename
                        )
                      ),
            },
          ];
        }

        const actions = [
          {
            label: 'Eliminar Evaluación',
            icon: 'fas fa-times-circle color-red',
            border: !showChangeDate ? 'bottom' : '',
            visible: canEliminateEvaluation,
            click: () => this.showDeleteCommentPeiRelated(evaluation),
          },
          {
            label: 'Cambiar Fecha',
            icon: 'fas fa-calendar color-green',
            border: 'bottom',
            visible: showChangeDate,
            click: () => this.showPreviousDateModal(evaluation),
          },
          {
            label: 'Documento de Evaluación',
            icon: 'fas fa-file-download color-green',
            visible: evaluation.hasDocument,
            click: () =>
              evaluation.peiAtServiceId > 0
                ? this.downloadAtServiceDocument(evaluation)
                : this.downloadDocument({
                    id: evaluation.studentEvaluationId,
                    type: 1,
                  }),
          },
          {
            label: 'Documento Minuta',
            icon: 'fas fa-file-alt color-green',
            visible: evaluation.studentEvaluationDocuments?.some(
              (d) => d.documentTypeId === 2
            ),
            click: () =>
              this.downloadDocument({
                id: evaluation.studentEvaluationId,
                type: 2,
              }),
          },
          {
            label: 'Reemplazar Documento de Evaluación',
            icon: 'fas fa-copy color-green',
            visible: evaluation.hasDocument && this.canReplaceDocument,
            click: () => this.replaceDocument({ evaluation, type: 1 }),
          },
          {
            label: 'Añadir Documento de Evaluación',
            icon: 'fas fa-save color-green',
            visible: !evaluation.hasDocument && this.canReplaceDocument,
            click: () => this.addDocument({ evaluation, type: 1 }),
          },
          {
            label: 'Descargar SAEE03',
            icon: 'fas fa-download color-green',
            visible: this.canDownloadSaee03(evaluation),
            click: () => this.downloadSaee03(evaluation),
          },
        ];

        if (store.getters.HasPermission(scopes.edit) && !evaluation.cantEdit) {
          actions.splice(1, 0, {
            label: 'Editar',
            icon: 'fas fa-pencil color-green',
            click: () =>
              this.toEvaluationHistoryEdit(evaluation.studentEvaluationId),
          });
        }

        if (
          this.canChangeToPending &&
          (evaluation.isApprovedByParentFlag !== null ||
            evaluation.discussionStatusId !== null)
        ) {
          actions.splice(1, 0, {
            label: 'Cambiar a Pendiente',
            icon: 'fas fa-undo color-green',
            click: () => this.changeStatusToPending(evaluation),
          });
        }

        if (
          this.canRestorePreviousDiscussionStatus &&
          evaluation.canRestorePreviousDiscussionStatus
        ) {
          actions.splice(1, 0, {
            label: 'Cambiar a estatus anterior',
            icon: 'fas fa-undo color-green',
            click: () => this.restorePreviousDiscussionStatus(evaluation),
          });
        }

        return actions;
      };
    },
    canChangePreviousDate() {
      return store.getters.HasPermission(
        'student.evaluation.pei.changepreviousdate'
      );
    },
    canChangeToPending() {
      return store.getters.HasPermission('student.evaluation.pei.changestatus');
    },
    canRestorePreviousDiscussionStatus() {
      return store.getters.HasPermission(
        'academic.student.evaluation.change.previousstatus'
      );
    },
    canDeletePeiRelated() {
      return store.getters.HasPermission('student.evaluation.pei.delete');
    },
    groupedEvaluations() {
      const options = this.evaluations.sort((a, b) =>
        a.disciplineName.localeCompare(b.disciplineName)
      );
      return options.reduce((r, a) => {
        r[a.disciplineName] = r[a.disciplineName] || [];
        r[a.disciplineName].push(a);
        return r;
      }, {});
    },
    canReplaceDocument() {
      return store.getters.HasPermission(
        'student.evaluation.canreplacedocument'
      );
    },
    sourceNameTypes() {
      const types = { Previa: 'Previa' };
      return types;
    },
    canDownloadSaee03() {
      return (evaluation) =>
        evaluation.discussionStatusId === DiscussionStatus.Rejected ||
        evaluation.disciplineId === disciplineTypes.Ocupacional ||
        evaluation.determinationStatusId ===
          ElegibilityDetermination.NegativeDetermination;
    },
  },
  watch: {
    rows(value) {
      this.$store.dispatch('student-evaluation-history/setEvaluations', value);
    },
  },
  methods: {
    getStatusDesc({ discussionStatus }) {
      return discussionStatus;
    },
    getStatusColor({ discussionStatusId }) {
      let classStr = 'secondary-5';

      switch (discussionStatusId) {
        case discussionStatuses.accepted:
        case discussionStatuses.partialAccepted:
          classStr = 'green-light';
          break;
        case discussionStatuses.rejected:
          classStr = 'brown';
          break;
        default:
          classStr = 'yellow';
          break;
      }

      return classStr;
    },
    async showDeleteConfirmation(evaluation) {
      await alert.methods
        .ShowCancelModal(
          'Atención',
          '¿Está seguro que desea eliminar este registro de evaluación?',
          'Sí',
          'No'
        )
        .then((answer) => {
          if (answer) {
            this.currentEvaluation = evaluation.studentEvaluationId;
            this.deleteEvaluation();
          }
        });
    },
    showPreviousDateModal(evaluation) {
      this.currentEvaluation = evaluation.studentEvaluationId;
      this.showPreviousDate = true;
      this.previousDate = evaluation.date;
    },
    showDeleteCommentPeiRelated(evaluation) {
      this.currentEvaluation = evaluation.studentEvaluationId;
      this.showDeleteComment = true;
      this.confirmDelete = true;
    },
    toEvaluationHistoryEdit(id) {
      this.$store.dispatch(
        'student-evaluation-history/setCurrentEvaluationId',
        id
      );
      this.$router.push({ name: 'student-evaluation-history-edit' });
    },
    async changeStatusToPending(evaluation) {
      this.currentEvaluation = evaluation.studentEvaluationId;
      this.confirmStatusChange = true;
    },
    async changeDeterminationToPending() {
      if (this.showStatusComment) {
        const isValid = await this.$refs.observer.validate();
        if (!isValid) {
          this.ShowToast(
            'Error',
            'Favor verificar y completar la información solicitada.',
            'error'
          );
          return;
        }
      }
      await studentEvaluationService
        .changeDeterminationToPending(
          this.currentEvaluation,
          this.statusComment
        )
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso.',
            'La evaluación fue cambiada a pendiente exitosamente.',
            'success'
          );
          this.closeModals();
          this.$emit('refresh-evaluations');
        })
        .catch(({ response: errors }) => {
          this.ShowToast('', errors.data.errors.errors[0], 'error');
        });
    },
    async restorePreviousDiscussionStatus(evaluation) {
      const answer = await alert.methods.ShowCancelModal(
        'Atención',
        '¿Está seguro que desea cambiar la evaluación a su estatus anterior?',
        'Sí',
        'No'
      );

      if (!answer) {
        return;
      }

      await studentEvaluationService
        .restorePreviousDiscussionStatus(evaluation.studentEvaluationId)
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso.',
            'La evaluación fue cambiada a su estatus anterior exitosamente.',
            'success'
          );
          this.$emit('refresh-evaluations');
        })
        .catch(({ response: errors }) => {
          this.ShowToast('', errors.data.errors.errors[0], 'error');
        });
    },
    async changePreviousDate() {
      if (this.currentEvaluation === null) {
        return;
      }

      const newEvaluationDate = new Date(this.previousDate).toISOString();

      await studentEvaluationService
        .changePreviousDate(this.currentEvaluation, newEvaluationDate)
        .catch(({ response: errors }) => {
          this.ShowToast('', errors.data.errors.errors[0], 'error');
        });

      this.ShowToast(
        'Proceso Exitoso.',
        'La fecha previa de la evaluación fue cambiada exitosamente.',
        'success'
      );
      this.closeModals();
      this.$emit('refresh-evaluations');

      this.closeModals();
    },
    async deleteEvaluation() {
      if (this.currentEvaluation === null) {
        return;
      }

      if (this.showDeleteComment) {
        const isValid = await this.$refs.observer.validate();
        if (!isValid) {
          this.ShowToast(
            'Error',
            'Favor verificar y completar la información solicitada.',
            'error'
          );
          return;
        }
      }

      this.$ApiDelete(
        `/api/studentevaluation/delete/${this.currentEvaluation}`,
        { comment: this.deleteComment }
      )
        .then(() => {
          this.ShowToast(
            'Proceso Exitoso.',
            'La evaluación fue eliminada exitosamente.',
            'success'
          );
          this.closeModals();
          this.$emit('refresh-evaluations');
        })
        .catch(({ response: errors }) => {
          this.ShowToast('', errors.data.errors.errors[0], 'error');
        });

      this.closeModals();
    },

    closeModals() {
      this.currentEvaluation = null;
      this.showCantDelete = false;
      this.showEvalModal = false;
      this.confirmDelete = false;
      this.showDeleteComment = false;
      this.showPreviousDate = false;
      this.deleteComment = null;
      this.confirmStatusChange = false;
      this.statusComment = null;
    },
    async downloadAtServiceDocument(evaluation) {
      await generalService
        .getDocument(
          evaluation.studentEvaluationDocuments[0].documentAttachmentId
        )
        .catch(({ response: errors }) => {
          this.ShowToast('', errors.data.errors.errors[0], 'error');
        });
    },
    downloadDocument(item) {
      generalService
        .downloadStudentDocument(item.id, item.type)
        .then((res) => {
          if (!res) {
            this.showEvalModal = true;
          }
        })
        .catch(({ response: errors }) => {
          this.ShowToast('', errors.data.errors.errors[0], 'error');
        });
    },
    async replaceDocument(row) {
      const confirmation = await this.ShowCancelModal(
        'Atención',
        '¿Esta seguro que desea reemplazar el documento?',
        'Aceptar',
        'Cancelar'
      );

      if (!confirmation) return;

      const inputElement = document.createElement('input');

      inputElement.setAttribute('type', 'file');
      inputElement.setAttribute('style', 'visibility:hidden');
      inputElement.setAttribute('accept', 'application/pdf');

      inputElement.onchange = async (event) => {
        const files = event.target.files;

        if (files.length === 0) return;

        await studentEvaluationService.replaceDocument(
          {
            studentEvaluationId: row.evaluation.studentEvaluationId,
            documentType: row.type,
          },
          files[0]
        );

        this.ShowToast(
          'Proceso Exitoso.',
          'La evaluación fue remplazada exitosamente.',
          'success'
        );

        inputElement.remove();
      };

      document.body.appendChild(inputElement);
      inputElement.click();
    },
    async addDocument(row) {
      const inputElement = document.createElement('input');

      inputElement.setAttribute('type', 'file');
      inputElement.setAttribute('style', 'visibility:hidden');
      inputElement.setAttribute('accept', 'application/pdf');

      inputElement.onchange = async (event) => {
        const files = event.target.files;

        if (files.length === 0) return;

        await studentEvaluationService.addDocument(
          {
            studentEvaluationId: row.evaluation.studentEvaluationId,
            documentType: row.type,
          },
          files[0]
        );

        this.ShowToast(
          'Proceso Exitoso.',
          'La evaluación fue añadida exitosamente.',
          'success'
        );

        inputElement.remove();
      };

      document.body.appendChild(inputElement);
      inputElement.click();
      row.evaluation.hasDocument = true;
    },
    async downloadSaee03(evaluation) {
      try {
        if (evaluation?.studentEvaluationId) {
          const studentEvaluationIds = [evaluation.studentEvaluationId];
          await reportService.downloadMultipleSaee03cReports(
            studentEvaluationIds
          );
        } else {
          this.ShowToast(
            'Alerta',
            'reporte no puede ser descargado.',
            'warning'
          );
          return;
        }
      } catch (err) {
        this.ShowToast('Alerta', 'Error al descargar el reporte.', 'error');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/_colors.scss';

.evaluations-wrapper {
  .data-title {
    display: block;
  }
  ::v-deep .custom-icon-view {
    margin-bottom: 0.5rem;
    .dot {
      box-shadow: none !important;
      margin-right: 0.5rem !important;
      .title-icon {
        margin: 0 !important;
      }
    }
    .font-16.color-gray-7 {
      font-size: 1rem !important;
      color: map-get($colors, 'gray-7') !important;
    }
  }

  ::v-deep .col.p-2.ml-2 .col-12 {
    padding-left: 0 !important;
  }
}
</style>
